import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useState } from "react";
import type { useForm } from "react-hook-form";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions } from "../../../session/permissions";
import { isPlanGateError } from "../shared/plan-gate";
import type { WidgetOut } from "../shared/types";
import type { AgentStatus } from "./agent-not-live-notice";
import type { PublishScope } from "./publish-choice-dialog";
import { useAgentLifecycle } from "./use-agent-lifecycle";
import { usePublish, useSaveDraft } from "./use-widget-studio";
import { enforceBrandingPlan, storedLeadFields, toDraftIn, toWidgetForm, type WidgetForm } from "./widget-model";

const K = "deployKnowledge.widgetStudio";

interface DraftActionsOptions {
  agentId: string;
  widget: WidgetOut;
  form: ReturnType<typeof useForm<WidgetForm>>;
  brandingLocked: boolean;
  agentStatus: AgentStatus | undefined;
}

/**
 * Save-draft and Publish for the studio. Publishing the widget of a *draft* agent asks whether to
 * publish the agent too (the widget cannot load until the agent is live); users who may not
 * publish agents get the widget published plus a toast saying an admin has to do the rest.
 */
export function useDraftActions({ agentId, widget, form, brandingLocked, agentStatus }: DraftActionsOptions) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const permissions = usePermissions(agentId);
  const save = useSaveDraft(agentId);
  const publish = usePublish(agentId);
  const lifecycle = useAgentLifecycle(agentId);
  const [planGated, setPlanGated] = useState(false);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [pendingScope, setPendingScope] = useState<PublishScope | null>(null);
  const agentIsDraft = agentStatus === "draft";
  const canPublishAgent = !permissions.readOnly && permissions.can("agents.publish", { agentId });

  const onError = (error: unknown) => {
    if (isPlanGateError(error)) setPlanGated(true);
    toast.error(message(error));
  };
  const invalid = () => toast.error(t(`${K}.invalidForm`));
  const persist = async (values: WidgetForm) => {
    const saved = await save.mutateAsync(toDraftIn(enforceBrandingPlan(values, brandingLocked), storedLeadFields(widget.draft)));
    form.reset(toWidgetForm(saved.draft));
  };
  const announcePublished = (scope: PublishScope) => {
    if (scope === "both") toast.success(t(`${K}.publishedBoth`));
    else if (agentIsDraft) toast.warning(t(canPublishAgent ? `${K}.publishedAgentDraft` : `${K}.publishedAgentNeedsAdmin`));
    else toast.success(t(`${K}.published`));
  };
  const runPublish = async (values: WidgetForm, scope: PublishScope) => {
    setPendingScope(scope);
    try {
      if (form.formState.isDirty) await persist(values);
      await publish.mutateAsync();
      if (scope === "both") await lifecycle.publish.mutateAsync();
      announcePublished(scope);
      setChoiceOpen(false);
    } catch (error) {
      onError(error);
    } finally {
      setPendingScope(null);
    }
  };

  const saveDraft = form.handleSubmit((values) => persist(values).then(() => toast.success(t(`${K}.draftSaved`)), onError), invalid);
  const publishAs = (scope: PublishScope) => form.handleSubmit((values) => runPublish(values, scope), invalid);
  const openChoice = form.handleSubmit(() => { setChoiceOpen(true); }, invalid);

  return {
    saveDraft,
    /** The Publish button: asks first when the agent is a draft and this user could publish it too. */
    requestPublish: () => (agentIsDraft && canPublishAgent ? openChoice() : publishAs("widget")()),
    choosePublishScope: (scope: PublishScope) => publishAs(scope)(),
    choiceOpen,
    setChoiceOpen,
    pendingScope,
    saving: save.isPending,
    publishing: pendingScope !== null,
    planGated,
  };
}
